import { ThemeComponent } from "@/theme/ThemeComponent";
import { OptionSelector } from "@/utils/OptionSelectors";
import { Component, Prop, Ref, SimpleEvent, Watch } from "@eastsideco/salvo-ts";
import { ProductOption } from "./ProductOptionPicker";
import { formatMoney } from "@/utils/formatting";

interface ValueRadio extends HTMLInputElement {
    dataset: {
        value: string;
    };
}
interface VariantAdditional {
    id: number;
    short: string | null;
    imgsrc: string;
}

class PriceManager {
    private static instance: PriceManager;
    public totalPrice: number = 0;

    private constructor() {}

    static getInstance() {
        if (!PriceManager.instance) {
            PriceManager.instance = new PriceManager();
        }
        return PriceManager.instance;
    }

    addPrice(amount: number) {
        this.totalPrice += amount;
    }

    subtractPrice(amount: number) {
        this.totalPrice -= amount;
    }

    getTotalPrice() {
        return this.totalPrice;
    }

    resetPrice() {
        this.totalPrice = 0;
    }
}

@Component
export class ProductMultiOption extends ThemeComponent implements ProductOption {
    @Prop name!: string;
    @Prop values!: string[];
    @Prop selected!: string | null;
    @Prop position!: number;
    @Prop({ default: null }) variants!: VariantAdditional[];
    @Ref buttons!: ValueRadio[];
    @Prop({ default: false }) noSelect!: boolean;
    @Ref({ required: false }) noSelectOption!: HTMLLabelElement;

    public nonSelected = this.noSelect || false;
    private valuesWithoutStock: string[] = [];
    private valuesWithoutVariant: string[] = [];
    private _onChange = new SimpleEvent<string | null>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    targetVariants: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedVariant: any;
    private totalPrice: number = 0;

    public get onChange() {
        return this._onChange.asEvent();
    }

    private open: string = '';
    private _onOpenChange = new SimpleEvent<string>();

    public get onOpenChange() {
        return this._onOpenChange.asEvent();
    }

    public changeOpen(open: string) {
        this.open = open;

        // Use the private backing field to dispatch
        this._onOpenChange.dispatch(open);
    }
    private close: string = '';
    private _onCloseChange = new SimpleEvent<string>();

    public get onCloseChange() {
        return this._onCloseChange.asEvent();
    }

    public changeClose(close: string) {
        this.close = close;

        // Use the private backing field to dispatch
        this._onCloseChange.dispatch(close);
    }

    private selectedVariants: { [key: string]: string | null } = {};


    @Watch('targetVariants')
    protected handeVariantsChange() {
        this.updateButtonStyles();
    }

    init() {
        const options = this.querySelectorAll('.product-form__button-item--addons');
        const inputContainer = document.querySelector('.options-wrap-js');
        const submitButton = document.querySelector('.options-submit-js') as HTMLButtonElement | null;

        for (const option of options) {
            option.addEventListener('click', () => {
                const priceManager = PriceManager.getInstance();
                const priceElement = option.querySelector('.product-form__button-price');
                const price = priceElement ?.innerHTML;
                const optionPrice = priceElement
                    ? parseFloat(priceElement.textContent?.replace(/[^0-9.-]+/g, '') || '0')
                    : 0;
                const state = option.querySelector('.product-form__button-state');
                const remove = option.querySelector('.product-form__button-remove') as HTMLBodyElement;
                const optionId = option.getAttribute('data-optionId');
                if (optionId) {
                    if (this.selectedVariants[optionId]) {
                        return;
                    } else {
                        option.classList.add('is-active');
                        this.selectedVariants[optionId] = optionId;
                        const currentIndex = Object.keys(this.selectedVariants).length - 1;
                        const newInput = document.createElement('input');
                        newInput.setAttribute('type', 'hidden');
                        newInput.setAttribute('name', `items[${currentIndex}][id]`);
                        newInput.setAttribute('value', `${optionId}`); // Set the value as id-[optionId]
                        priceManager.addPrice(optionPrice);
                        inputContainer?.appendChild(newInput);
                        this.totalPrice += optionPrice;
                        if(submitButton){
                            submitButton.removeAttribute('disabled');
                            this.updatePrice(submitButton);
                        }
                        if (state) {
                            state.textContent = 'selected';
                        }
                        if (remove) {
                            remove.textContent = 'Remove';
                            remove.onclick = (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (this.selectedVariants[optionId]) {
                                    delete this.selectedVariants[optionId];
                                    option.classList.remove('is-active');
                                    priceManager.subtractPrice(optionPrice);
                                    // Remove the corresponding input from the inputContainer
                                    const inputToRemove = inputContainer?.querySelector(`input[value="${optionId}"]`);
                                    if (inputToRemove) {
                                        inputContainer?.removeChild(inputToRemove);
                                    }

                                    // Remove the corresponding summary item
                                    const selectedOptionId = optionId.replace(/^[^-]+-/, '');
                                    const targetSummary =
                                    document.querySelector(`.product__summary-item input[value="${selectedOptionId}"]`);
                                    const summaryItem = targetSummary?.parentElement;
                                    if (summaryItem) {
                                        summaryItem.remove();
                                    }

                                    document.dispatchEvent(new Event('addonUpdated'));

                                    if (state) {
                                        state.textContent = '';
                                    }
                                    if (remove) {
                                        remove.textContent = '+' + price;
                                    }

                                    this.totalPrice -= optionPrice;

                                    if (submitButton) {
                                        if (Object.keys(priceManager).length === 0) {
                                            submitButton.setAttribute('disabled', 'true');
                                            submitButton.value = `Add to Cart`;
                                        } else {
                                            submitButton.removeAttribute('disabled');
                                            this.updatePrice(submitButton);
                                        }
                                    }
                                }
                            };
                        }
                    }
                }
                // Update the selected IDs and set the attribute
                // const selectedIds = Object.keys(this.selectedVariants);
                // const selectedIdsString = JSON.stringify(selectedIds); // Convert to a JSON string
                // this.setAttribute('data-optionSelected', selectedIdsString);
                const addonEvent = new CustomEvent('addonUpdated', {
                    detail: { selectedVariants: this.selectedVariants },
                });
                document.dispatchEvent(addonEvent);
            });
        }

        this.updateButtonStyles();

        document.addEventListener('clearOptions', () => {
            this.clearOptions();
        });
    }

    public getSelectedValues(): { [key: string]: string | null } {
        return this.selectedVariants;
    }

    public select(value: string | null) {
        this.selected = value;
        this.updateButtonStyles();
        this._onCloseChange.dispatch(this.close);
    }

    public updateBasedOnSelector(selector: OptionSelector) {
        this.valuesWithoutStock = selector.options[this.position - 1].values
            .filter((v) => v.outOfStock).map((v) => v.value);
        this.valuesWithoutVariant = selector.options[this.position - 1].values
            .filter((v) => !v.variantExists).map((v) => v.value);
        this.targetVariants = selector.options[this.position - 1].values
            .filter((v) => v.targetVariant).map((v) => v.targetVariant);
    }

    private updateButtonStyles() {
        for (const [index, button] of this.buttons.entries()) {
            if (button.parentNode == null) { return; }
            const $wrapper = button.parentNode as HTMLElement;
            const img = $wrapper.querySelector('.product-form__button-item-image');
            if (!this.targetVariants) { return; }
            const target = this.targetVariants[index];
            if (!target) { return; }

            if (img) {
                img.innerHTML = '';
            }
            if (!this.variants) { return; }
            const chosen = this.variants.find((i: VariantAdditional) => i.id == target.id);
            if (!chosen) { return; }
            if (img) {
                if (chosen.imgsrc) {
                    img.innerHTML = `<img src="${chosen.imgsrc}" alt="${target.title}">`;
                    img.classList.remove('product-form__button-item-image--empty');
                } else {
                    img.classList.add('product-form__button-item-image--empty');
                }
            }

            if (chosen.id) {
                $wrapper.removeAttribute('data-optionid');
                $wrapper.setAttribute('data-optionid', `${button.value}-${chosen.id}`);

                // display selected options dynamically in summary
                const selectedOptions = $wrapper.querySelector('.product-form__selected-option span');
                const otherSelectedOptions =
                document.querySelectorAll(
                    '.product-form__add-on-drawer .product-option-picker [data-ref="selectedValue"]');
                if (selectedOptions) {
                    let otherOptionContent = '';
                    if (otherSelectedOptions.length > 0) {
                        for(const otherSelectedOption of otherSelectedOptions) {
                            otherOptionContent += otherSelectedOption.innerHTML;
                        }
                        if (button.value !== "Default Title") {
                            selectedOptions.innerHTML = " - " + (otherOptionContent ? otherOptionContent + " / " : "")
                            + button.value;
                        }else {
                            selectedOptions.innerHTML = " - " + otherOptionContent;
                        }
                    }
                }

            }

            this.clearOptions();
        }

        // show selected option in addon drawer
        // const mainSelectedOption = document.querySelector(
        //     '.product-form__drawer-toggle span[data-ref="selectedValue"]'
        // ) as HTMLElement | null;
        // if(!mainSelectedOption?.innerHTML.includes("Please select")) {
        //     const options = document.querySelectorAll('.product-form__button-item--addons');
        //     for (const option of options) {
        //         const selectedOptionText= option.querySelector('.product-form__button-option') as
        //         HTMLButtonElement | null;
        //         if (selectedOptionText && mainSelectedOption) {
        //             selectedOptionText.innerHTML = mainSelectedOption.innerHTML;
        //         }
        //     }
        // }
    }

    private clearOptions() {
        const inputContainer = document.querySelector('.options-wrap-js');
        const submitButton = document.querySelector('.options-submit-js') as HTMLButtonElement | null;

        this.selectedVariants = {};

        const options = this.querySelectorAll('.product-form__button-item--addons');
        for (const option of options) {
            option.classList.remove('is-active');
            const state = option.querySelector('.product-form__button-state');
            const remove = option.querySelector('.product-form__button-remove') as HTMLBodyElement;
            const priceElement = option.querySelector('.product-form__button-price');
            const price = priceElement ?.innerHTML;
            if (state) {
                state.textContent = '';
            }
            if (remove) {
                remove.textContent = '+' + price;
            }
        }

        // Remove all inputs from the input container
        if (inputContainer) {
            const inputsToRemove = inputContainer.querySelectorAll('input[type="hidden"]');
            for (const input of inputsToRemove) {
                inputContainer.removeChild(input);
            }
        }

        // this.totalPrice = 0;
        const priceManager = PriceManager.getInstance();
        priceManager.resetPrice();
        if (submitButton) {
            submitButton.setAttribute('disabled', 'true');
            submitButton.value = `Add to Cart`;
        }

        const targetSummaryItems = document.querySelectorAll(`.product__summary-item--addons`);
        for (const targetSummary of targetSummaryItems){
            targetSummary.remove();
        }

        document.dispatchEvent(new Event('addonUpdated'));
    }

    private updatePrice(submitButton: HTMLButtonElement) {
        const priceManager = PriceManager.getInstance(); // Get the shared price manager instance
        const totalPrice = priceManager.getTotalPrice() * 100; // Convert to cents
        submitButton.value = `Add to Cart | ${formatMoney(totalPrice)}`;
    }

}

